
import { Camera, Trash2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

interface ProfilePictureProps {
  profileId: string;
  pictureUrl: string | null;
  onUpdate: () => Promise<void>;
}

export const ProfilePicture = ({ profileId, pictureUrl, onUpdate }: ProfilePictureProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      const fileExt = file.name.split('.').pop();
      const filePath = `${profileId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('organization_members')
        .update({ profile_picture_url: publicUrl })
        .eq('id', profileId);

      if (updateError) throw updateError;

      await onUpdate();
      toast({
        title: "Success",
        description: "Profile picture updated successfully",
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: "Error",
        description: "Failed to upload profile picture",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePicture = async () => {
    if (!pictureUrl) return;
    
    try {
      setSaving(true);
      
      const { error: updateError } = await supabase
        .from('organization_members')
        .update({ profile_picture_url: null })
        .eq('id', profileId);

      if (updateError) throw updateError;

      await onUpdate();
      toast({
        title: "Success",
        description: "Profile picture removed successfully",
      });
    } catch (error) {
      console.error('Error removing profile picture:', error);
      toast({
        title: "Error",
        description: "Failed to remove profile picture",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <div
        className="bg-center bg-no-repeat aspect-square bg-cover rounded-full min-h-32 w-32 relative"
        style={{ 
          backgroundImage: `url("${pictureUrl || 'https://via.placeholder.com/128'}")` 
        }}
      >
        <label
          htmlFor="profile-picture"
          className="absolute bottom-2 right-2 p-2 bg-[#2F80ED] hover:bg-[#1B365D] rounded-full cursor-pointer transition-colors"
        >
          <Camera className="h-4 w-4 text-white" />
          <input
            type="file"
            id="profile-picture"
            accept="image/*"
            onChange={handleProfilePictureUpload}
            className="hidden"
            disabled={saving}
          />
        </label>
      </div>
      
      {pictureUrl && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRemovePicture} 
          disabled={saving}
          className="mt-2 h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 mx-auto block"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove picture
        </Button>
      )}
    </div>
  );
};
